/**
 * Mapeo de presentación del motor operativo de 5 estados (EVALPRO v2.2).
 * NO prescribe ejercicio: solo informa la conducta operativa por estado.
 * Espejo de backend/decision_presentation.py
 */

const FALLBACK = {
  estado: 'PUEDE_INICIAR',
  headline: 'Inicio habilitado',
  bannerColor: 'verde',
  bannerClasses: 'bg-green-50 border-green-200 text-green-800',
  iconBgClasses: 'bg-green-200',
  iconColorClasses: 'text-green-700',
  moderate: 'Habilitado',
  vigorous: 'Habilitado',
  medicalEval: 'No se identifican condiciones que sugieran consulta médica previa.',
  conducta:
    'El sistema habilita ejercicio leve a moderado y vigoroso. No se identifican condiciones que sugieran consulta médica previa.',
};

const PRESENTATION_BY_ESTADO = {
  PUEDE_INICIAR: { ...FALLBACK },

  APTO_PARA_EJERCICIO_MODERADO: {
    estado: 'APTO_PARA_EJERCICIO_MODERADO',
    headline: 'Inicio prudente',
    bannerColor: 'amarillo_claro',
    bannerClasses: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    iconBgClasses: 'bg-yellow-200',
    iconColorClasses: 'text-yellow-800',
    moderate: 'Habilitado',
    vigorous: 'Habilitado — progresión gradual',
    medicalEval: 'No se requiere consulta médica en ausencia de síntomas.',
    conducta:
      'El sistema habilita ejercicio leve a moderado. Puede progresar gradualmente a ejercicio vigoroso siguiendo las guías ACSM, en ausencia de síntomas.',
  },

  APTO_PARA_PROGRESAR_CON_CONTROL: {
    estado: 'APTO_PARA_PROGRESAR_CON_CONTROL',
    headline: 'Continúa con seguimiento',
    bannerColor: 'amarillo',
    bannerClasses: 'bg-amber-50 border-amber-300 text-amber-900',
    iconBgClasses: 'bg-amber-200',
    iconColorClasses: 'text-amber-800',
    moderate: 'Habilitado',
    vigorous: 'Consulta médica recomendada antes de progresar',
    medicalEval: 'Consulta médica recomendada antes de progresar a ejercicio vigoroso.',
    conducta:
      'El sistema habilita continuar con ejercicio leve a moderado. Consulta médica recomendada antes de progresar a ejercicio vigoroso.',
  },

  APTO_REQUERIDO: {
    estado: 'APTO_REQUERIDO',
    headline: 'Consulta médica recomendada',
    bannerColor: 'naranja',
    bannerClasses: 'bg-orange-50 border-orange-300 text-orange-900',
    iconBgClasses: 'bg-orange-200',
    iconColorClasses: 'text-orange-800',
    moderate: 'Consulta médica recomendada antes de iniciar',
    vigorous: 'Consulta médica recomendada antes de iniciar',
    medicalEval: 'Consulta médica recomendada antes de iniciar ejercicio estructurado.',
    conducta:
      'Consulta médica recomendada antes de iniciar ejercicio estructurado. Tras la consulta, comenzar con intensidad leve a moderada y progresar gradualmente según tolerancia.',
  },

  DERIVACION_INMEDIATA: {
    estado: 'DERIVACION_INMEDIATA',
    headline: 'Requiere evaluación médica',
    bannerColor: 'rojo',
    bannerClasses: 'bg-red-50 border-red-300 text-red-900',
    iconBgClasses: 'bg-red-200',
    iconColorClasses: 'text-red-700',
    moderate: 'No iniciar — derivación médica',
    vigorous: 'No iniciar — derivación médica',
    medicalEval: 'Derivación médica previa a cualquier intervención estructurada.',
    conducta:
      'Derivación médica previa a cualquier intervención estructurada de ejercicio.',
  },
};

// Aliases legacy → nuevos (para evaluaciones viejas en BD aún no migradas).
const LEGACY_ALIAS = {
  APTO_RECOMENDABLE: 'APTO_PARA_EJERCICIO_MODERADO',
  APTO_PARA_PROGRESAR: 'APTO_PARA_PROGRESAR_CON_CONTROL',
};

function normalizeEstado(estado) {
  if (!estado) return 'PUEDE_INICIAR';
  return LEGACY_ALIAS[estado] || estado;
}

export function getDecisionPresentation(estado) {
  const canonical = normalizeEstado(estado);
  return PRESENTATION_BY_ESTADO[canonical] || FALLBACK;
}

// ============================================================================
// INTENSITY UNLOCK — Single source of truth (frontend). Espejo de
// backend/decision_presentation.py :: intensity_unlock().
// ============================================================================
const UNLOCK_RULES = {
  PUEDE_INICIAR:                    { light: true,  moderate: true,  vigorous: true  },
  APTO_PARA_EJERCICIO_MODERADO:     { light: true,  moderate: true,  vigorous: true  },
  APTO_PARA_PROGRESAR_CON_CONTROL:  { light: true,  moderate: true,  vigorous: false },
  APTO_REQUERIDO:                   { light: false, moderate: false, vigorous: false },
  DERIVACION_INMEDIATA:             { light: false, moderate: false, vigorous: false },
};

const UNLOCK_REASON = 'Se sugiere evaluación médica antes de habilitar esta intensidad.';

/**
 * Devuelve qué intensidades están habilitadas según el escenario del motor
 * y el apto médico entregado. Capa de presentación — no toca el motor.
 *
 * @param {string} estado - Nombre interno del estado (soporta alias legacy).
 * @param {boolean} aptoEntregado - evaluation.apto_medico_entregado.
 * @param {string[]} aptoIntensidades - evaluation.apto_intensidades_habilitadas.
 * @returns {{light:{enabled:boolean,reason:string|null}, moderate:{enabled:boolean,reason:string|null}, vigorous:{enabled:boolean,reason:string|null}}}
 */
export function intensityUnlock(estado, aptoEntregado = false, aptoIntensidades = []) {
  const canonical = normalizeEstado(estado || 'PUEDE_INICIAR');
  const base = UNLOCK_RULES[canonical] || UNLOCK_RULES.APTO_REQUERIDO;
  const list = (Array.isArray(aptoIntensidades) ? aptoIntensidades : [])
    .map((x) => String(x || '').trim().toLowerCase());
  const aptoLight = !!aptoEntregado && list.includes('leve');
  const aptoModerate = !!aptoEntregado && list.includes('moderada');
  const aptoVigorous = !!aptoEntregado && list.includes('vigorosa');
  // El apto habilita también lo de menor intensidad: no tiene sentido
  // autorizar la mayor y bloquear la menor. Un apto solo para vigorosa
  // habilita moderada y leve. (Mismo criterio en decision_presentation.py.)
  const lightOn = base.light || aptoLight || aptoModerate || aptoVigorous;
  const modOn = base.moderate || aptoModerate || aptoVigorous;
  const vigOn = base.vigorous || aptoVigorous;
  return {
    light: { enabled: lightOn, reason: lightOn ? null : UNLOCK_REASON },
    moderate: { enabled: modOn, reason: modOn ? null : UNLOCK_REASON },
    vigorous: { enabled: vigOn, reason: vigOn ? null : UNLOCK_REASON },
  };
}

/**
 * Recomendaciones de fuerza por escenario del motor. Espejo de
 * backend/decision_presentation.py :: get_strength_recommendation().
 *
 * - Verde / amarillo claro / amarillo → tabla 3×2 + nota opcional.
 * - Naranja / rojo → solo mensaje de diferimiento (sin tabla).
 *
 * Banner classes para Tailwind: bg + border + text.
 */
const STRENGTH_TABLES = {
  PUEDE_INICIAR: {
    showTable: true,
    bannerColor: 'verde',
    bannerClasses: 'bg-green-50 border-green-200 text-green-800',
    labelClasses: 'text-green-700',
    rows: [
      ['Frecuencia',   '2-3 veces/sem'],
      ['Ejercicios',   '8-10 grupos'],
      ['Series',       '2-4 series'],
      ['Repeticiones', '8-12 reps'],
      ['Intensidad',   '60-70% 1RM'],
      ['Progresión',   'Gradual'],
    ],
    note: null,
  },
  APTO_PARA_EJERCICIO_MODERADO: {
    showTable: true,
    bannerColor: 'amarillo_claro',
    bannerClasses: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    labelClasses: 'text-yellow-800',
    rows: [
      ['Frecuencia',   '2 veces/sem'],
      ['Ejercicios',   '8-10 grupos'],
      ['Series',       '1-3 series'],
      ['Repeticiones', '10-15 reps'],
      ['Intensidad',   '50-60% 1RM'],
      ['Progresión',   'Muy gradual'],
    ],
    note: 'Iniciar con cargas livianas y progresar según tolerancia.',
  },
  APTO_PARA_PROGRESAR_CON_CONTROL: {
    showTable: true,
    bannerColor: 'amarillo',
    bannerClasses: 'bg-amber-50 border-amber-300 text-amber-900',
    labelClasses: 'text-amber-800',
    rows: [
      ['Frecuencia',   '2-3 veces/sem'],
      ['Ejercicios',   '8-10 grupos'],
      ['Series',       '2-3 series'],
      ['Repeticiones', '10-15 reps'],
      ['Intensidad',   '50-60% 1RM'],
      ['Progresión',   'Gradual'],
    ],
    note:
      'Mantener el régimen actual. Evaluación médica recomendada antes de progresar a cargas mayores.',
  },
  APTO_REQUERIDO: {
    showTable: false,
    bannerColor: 'naranja',
    bannerClasses: 'bg-orange-50 border-orange-300 text-orange-900',
    rows: [],
    note: null,
    deferredMessage:
      'Recomendaciones de fuerza diferidas hasta consulta médica.',
  },
  DERIVACION_INMEDIATA: {
    showTable: false,
    bannerColor: 'rojo',
    bannerClasses: 'bg-red-50 border-red-300 text-red-900',
    rows: [],
    note: null,
    deferredMessage:
      'Recomendaciones de fuerza diferidas hasta evaluación médica.',
  },
};

export function getStrengthRecommendation(estado) {
  const canonical = normalizeEstado(estado);
  return STRENGTH_TABLES[canonical] || STRENGTH_TABLES.PUEDE_INICIAR;
}

/**
 * Construye el contexto operativo del motor a partir de los datos de la
 * evaluación. Si el backend no envió `estado`, lo deriva.
 */
export function getDecisionFromEvaluation(evaluation) {
  if (!evaluation) return getDecisionPresentation('PUEDE_INICIAR');
  const rec = evaluation.exercise_recommendation || {};
  const estado = normalizeEstado(rec.estado || mapLegacyToEstado(evaluation));
  const presentation = getDecisionPresentation(estado);
  // Override de conducta operativa (cuando el motor lo emite: e.g. HTA sin
  // tratamiento + APTO_PARA_EJERCICIO_MODERADO).
  const conducta = rec.conducta_override || presentation.conducta;
  return {
    ...presentation,
    conducta,
    motivos: rec.motivos_decision || [],
    // Label visible on-the-fly: el `headline` actual gana sobre `rec.etiqueta`
    // persistido en docs viejos, para que el rename de escenarios se propague
    // sin migrar la base.
    etiqueta: presentation.headline,
  };
}

function mapLegacyToEstado(evaluation) {
  // Solo evaluaciones viejas que tenían risk_level pero no estado.
  if (evaluation.cardiovascular_alert) return 'DERIVACION_INMEDIATA';
  if (evaluation.risk_level === 'alto') return 'APTO_REQUERIDO';
  if (evaluation.risk_level === 'moderado') return 'APTO_PARA_PROGRESAR_CON_CONTROL';
  if (!['activo_regular', 'activa_regular'].includes(evaluation.activity_level)) {
    return 'APTO_PARA_EJERCICIO_MODERADO';
  }
  return 'PUEDE_INICIAR';
}


// === Espejo frontend del motor backend evaluate_decision() ============
// MUST stay in sync with backend/calculations.py::evaluate_decision()
// Usado en Step7 del wizard (preview pre-save) y en cualquier otro lugar
// donde se necesite evaluar decisión sin round-trip al backend.

const SYMPTOM_LABELS = {
  symptom_chest_pain: 'dolor/malestar en pecho durante reposo o actividad',
  symptom_neck_jaw_arm_pain: 'dolor o molestia en cuello, mandíbula o brazos',
  symptom_dyspnea: 'falta de aire inusual o con esfuerzos leves',
  symptom_dizziness: 'mareos o pérdida de conciencia durante actividad física',
  symptom_orthopnea: 'dificultad para respirar acostado o disnea nocturna',
  symptom_ankle_swelling: 'hinchazón persistente en tobillos',
  symptom_palpitations: 'palpitaciones o latidos irregulares frecuentes',
  symptom_intermittent_claudication: 'claudicación intermitente: dolor en las piernas al caminar que mejora con el reposo',
  symptom_heart_murmur: 'soplo cardíaco informado',
  symptom_unusual_fatigue: 'fatiga inusual con actividades habituales',
};

// Back-compat: campo legacy eliminado del cuestionario, se mapea al ítem unificado.
const LEGACY_SYMPTOM_ALIASES = {
  symptom_claudication: 'symptom_intermittent_claudication',
};

const PA_DANGEROUS_SBP = 160;
const PA_DANGEROUS_DBP = 100;

function _toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function _parseBP(raw) {
  if (raw === null || raw === undefined) return [null, null];
  const s = String(raw).trim();
  if (!s) return [null, null];
  const m = s.match(/(\d{2,3})\s*[/\\]\s*(\d{2,3})/);
  if (!m) return [null, null];
  return [parseInt(m[1], 10), parseInt(m[2], 10)];
}

function _isInactive(activityLevel) {
  // Espeja calculations.py:238 — TRUE si NO es 'activo_regular'/'activa_regular'.
  // Cualquier otro valor (sedentario, irregular, insuficientemente_activa, '' o null)
  // cuenta como inactivo. Robusto a alias del form que aún no estén mapeados.
  return !['activo_regular', 'activa_regular'].includes(activityLevel);
}

export function evaluateDecisionFE(formData) {
  const fd = formData || {};

  // 1. Síntomas activos (algún síntoma === true)
  // Back-compat: si una eval vieja tiene symptom_claudication=true, lo tratamos
  // como equivalente al ítem unificado (sin mutar formData).
  const _isSymptomTrue = (key) => {
    if (fd[key] === true) return true;
    for (const [legacy, canonical] of Object.entries(LEGACY_SYMPTOM_ALIASES)) {
      if (canonical === key && fd[legacy] === true) return true;
    }
    return false;
  };
  const sintomas = Object.keys(SYMPTOM_LABELS)
    .filter((k) => _isSymptomTrue(k))
    .map((k) => SYMPTOM_LABELS[k]);

  if (sintomas.length > 0) {
    return {
      estado: 'DERIVACION_INMEDIATA',
      etiqueta: 'Derivación médica previa — signos sugerentes de enfermedad CV/metabólica/renal según ACSM',
      color: 'rojo',
      motivos_decision: ['Signos referidos: ' + sintomas.join('; ')],
    };
  }

  // ACSM (Riebe et al., 2015): la PA elevada NO es criterio de derivación.
  // Se mantiene como dato informativo en el informe del profesional; la
  // advertencia de PA>=180/110 se resuelve en la capa de PDF, no aquí.

  // 3. Enfermedades declaradas (espejo exacto backend calculations.py::evaluate_decision)
  // Diabetes + 4 enfermedades duras cuentan SIEMPRE.
  // HTA y dislipemia NO disparan el árbol (con o sin tratamiento): son
  // factores de riesgo informativos. Alineado con ACSM/AHA/ESC.
  const enfermedades = [];
  if (fd.diabetes === true) {
    const dt = (fd.diabetes_type || '').trim();
    enfermedades.push(dt ? `Diabetes ${dt}` : 'Diabetes');
  }
  if (fd.cardiac_disease === true) enfermedades.push('Enfermedad cardíaca conocida');
  if (fd.cerebrovascular_disease === true) enfermedades.push('ACV o AIT previo');
  if (fd.peripheral_artery_disease === true) enfermedades.push('Enfermedad arterial periférica o aneurisma de aorta');
  if (fd.chronic_kidney_disease === true) enfermedades.push('Enfermedad renal crónica');
  const enfermedad_cv_metabolica = enfermedades.length > 0;

  // 4. Inactividad
  const inactivo = _isInactive(fd.activity_level);

  // Cascada de matcheo
  if (enfermedad_cv_metabolica && inactivo) {
    return {
      estado: 'APTO_REQUERIDO',
      etiqueta: 'Consulta médica recomendada antes de iniciar ejercicio estructurado',
      color: 'naranja',
      motivos_decision: ['Persona inactiva con enfermedad declarada: ' + enfermedades.join(', ')],
    };
  }

  if (enfermedad_cv_metabolica && !inactivo) {
    return {
      estado: 'APTO_PARA_PROGRESAR_CON_CONTROL',
      etiqueta: 'Apto para progresar con control — evaluación médica recomendada para ejercicio vigoroso',
      color: 'amarillo',
      motivos_decision: [
        'Persona activa con enfermedad declarada estable: ' + enfermedades.join(', '),
      ],
    };
  }

  if (inactivo) {
    const result = {
      estado: 'APTO_PARA_EJERCICIO_MODERADO',
      etiqueta: 'Apto para ejercicio moderado — progresión gradual a vigoroso según guías ACSM',
      color: 'amarillo_claro',
      motivos_decision: [
        'Persona inactiva sin enfermedad CV/metabólica declarada ni signos de alarma',
      ],
    };
    // Refuerzo de conducta operativa: HTA diagnosticada sin tratamiento.
    // No cambia el estado, solo el copy. Espejo exacto de calculations.py.
    if (fd.hypertension === true && fd.hypertension_treatment !== true) {
      result.conducta_override =
        'El sistema habilita ejercicio leve a moderado. Ante hipertensión diagnosticada ' +
        'sin tratamiento informado, se recomienda consulta médica antes de ' +
        'progresar a ejercicio vigoroso.';
      result.motivos_decision.push(
        'Hipertensión arterial diagnosticada sin tratamiento informado (factor de riesgo cardiovascular).'
      );
    }
    return result;
  }

  return {
    estado: 'PUEDE_INICIAR',
    etiqueta: 'Puede iniciar — sin restricciones',
    color: 'verde',
    motivos_decision: ['Persona activa, sin enfermedad declarada, sin signos de alarma'],
  };
}
